import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { registerOnChangeCallbacks } from '@root/utils/cookies/cookies';
import editorSidebarSlice from 'components/EditorSidebar/editorSidebarSlice';
import { createTransform, FLUSH, PAUSE, PERSIST, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { baseApi } from 'services/baseApi';
import reactionsSlice from 'slices/reactionsSlice';

import accountReducer from './slices/accountSlice';
import { updatedRefreshToken, updatedToken } from './slices/actions/tokenActions';
import actionsReducer from './slices/actionsSlice';
import auditSlice from './slices/auditSlice';
import authReducer from './slices/authSlice';
import billingReducer from './slices/billingSlice';
import desktopReducer from './slices/desktopSlice';
import flagsmithSlice from './slices/flagsmith/slice';
import knowledgePageSlice from './slices/knowledgePageSlice';
import managerReducer from './slices/managerSlice';
import newDashboardSlice from './slices/newDashboardSlice';
import organizationAdminSlice from './slices/organizationAdminSlice';
import roleSlice from './slices/roleSlice';
import screenshotEditingSlice from './slices/screenshotEditingSlice';
import scribeDocumentReducer from './slices/scribeDocumentSlice';
import scribeFileReducer from './slices/scribeFileSlice';
import searchSlice from './slices/searchSlice';
import superOrganizationAdminSlice from './slices/superOrganizationAdminSlice';
import superOrganizationSlice from './slices/superOrganizationSlice';
import teamReducer from './slices/teamSlice';
import workOsGroupReducer from './slices/workOsGroupSlice';
import writableScribeDocumentsSlice from './slices/writableScribeDocumentsSlice';
import documentDrawerSlice from './v2/slices/document-drawer/slice';
import documentReviewSlice from './v2/slices/document-review';
import documentsSlice from './v2/slices/documents/slice';
import folderSlice from './v2/slices/folders';
import sharingPermissionsSlice from './v2/slices/sharing-permissions/slice';
import sidebarSlice from './v2/slices/sidebar';
import userSlice from './v2/slices/user/slice';
import workspaceSlice from './v2/slices/workspace';

// make the root reducer
const rootReducer = combineReducers({
  account: accountReducer,
  scribeDocument: scribeDocumentReducer,
  scribeFile: scribeFileReducer,
  auth: authReducer,
  team: teamReducer,
  manager: managerReducer,
  desktop: desktopReducer,
  actions: actionsReducer,
  billing: billingReducer,
  superOrganization: superOrganizationSlice,
  search: searchSlice,
  organizationAdmin: organizationAdminSlice,
  superOrganizationAdmin: superOrganizationAdminSlice,
  writableScribeDocuments: writableScribeDocumentsSlice,
  audit: auditSlice,
  screenshotEditing: screenshotEditingSlice,
  roles: roleSlice,
  documentReview: documentReviewSlice,
  workspace: workspaceSlice,
  sidebar: sidebarSlice,
  editorSidebar: editorSidebarSlice,
  folders: folderSlice,
  flagsmith: flagsmithSlice,
  knowledgePage: knowledgePageSlice,
  pageDocuments: documentsSlice,
  pageDocumentDrawer: documentDrawerSlice,
  user: userSlice,
  newDashboard: newDashboardSlice,
  sharingPermissions: sharingPermissionsSlice,
  reactions: reactionsSlice,
  workOsGroup: workOsGroupReducer,
  baseApi: baseApi.reducer
});

const AuthTransform = createTransform(
  // transform state on its way to being serialized and persisted.
  (inboundState, key) => {
    return { ...inboundState };
  },
  // transform state being rehydrated
  (outboundState, key) => {
    // convert mySet back to a Set.
    return {
      ...outboundState,
      isError: false,
      errorMessage: null,
      isLoading: false
    };
  },
  // define which reducers this transform gets called for.
  { whitelist: ['auth'] }
);

const persistConfig = {
  key: 'root',
  whitelist: ['auth', 'account'],
  storage,
  transforms: [AuthTransform]
};
const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV !== 'production',
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    }).concat(baseApi.middleware)
});

const persistor = persistStore(store, null, () => {
  // https://github.com/rt2zz/redux-persist#persiststorestore-config-callback
  // store.dispatch({ type: 'REHYDRATED' }); // Remove?x
});

const tokenChangedCallback = (value) => {
  if (value) {
    store.dispatch(updatedToken(value));
  }
};
const refreshChangedCallback = (value) => {
  if (value) {
    store.dispatch(updatedRefreshToken(value));
  }
};

registerOnChangeCallbacks(tokenChangedCallback, refreshChangedCallback);

export { persistor, store };
